import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { dataService } from '@/services/data-service';
import type {
	WorkOrdersReportParams,
	WorkOrdersReportResponse,
} from '@/types/reports';

const WORK_ORDERS_REPORT_KEY = ['reports', 'work-orders'] as const;

type Updater<T> = T | ((previous: T) => T);

function toUpdaterValue<T>(value: Updater<T>, previous: T): T {
	return typeof value === 'function' ? (value as (prev: T) => T)(previous) : value;
}

function buildDefaultParams(initial?: WorkOrdersReportParams): Required<Pick<WorkOrdersReportParams, 'status' | 'sortBy' | 'sortDirection'>> & WorkOrdersReportParams {
	return {
		status: 'all',
		sortBy: 'orderDate',
		sortDirection: 'asc',
		startDate: initial?.startDate ?? null,
		endDate: initial?.endDate ?? null,
		associationId: initial?.associationId ?? null,
		pmId: initial?.pmId ?? null,
		technicianId: initial?.technicianId ?? null,
		...initial,
	};
}

export interface UseWorkOrdersReportResult {
	data: WorkOrdersReportResponse | undefined;
	isLoading: boolean;
	isFetching: boolean;
	error: Error | null;
	params: WorkOrdersReportParams;
	setParams: (value: Updater<WorkOrdersReportParams>) => void;
	reset: () => void;
}

export function useWorkOrdersReport(initialParams?: WorkOrdersReportParams): UseWorkOrdersReportResult {
	const defaults = useMemo(() => buildDefaultParams(initialParams), [initialParams]);
	const [params, setParamsState] = useState<WorkOrdersReportParams>(defaults);
	const latestParamsRef = useRef<WorkOrdersReportParams>(defaults);
	const isFirstRenderRef = useRef(true);
	const latestDataRef = useRef<WorkOrdersReportResponse | undefined>(undefined);
	const refetchResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const query = useQuery({
		queryKey: WORK_ORDERS_REPORT_KEY,
		queryFn: () => dataService.getWorkOrdersReport(latestParamsRef.current),
		placeholderData: (previousData) => previousData,
		networkMode: 'offlineFirst',
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});
	const {
		data,
		isLoading,
		isFetching: queryIsFetching,
		error,
		refetch,
	} = query;
	const [isRefetching, setIsRefetching] = useState(false);
	const [currentData, setCurrentData] = useState<WorkOrdersReportResponse | undefined>(undefined);

	useEffect(() => {
		latestParamsRef.current = params;
		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			return;
		}
		setCurrentData(prev => prev ?? latestDataRef.current);
		setIsRefetching(true);
		void refetch({ throwOnError: false })
			.finally(() => {
				if (refetchResetTimeoutRef.current) {
					clearTimeout(refetchResetTimeoutRef.current);
				}
				refetchResetTimeoutRef.current = setTimeout(() => {
					setIsRefetching(false);
					refetchResetTimeoutRef.current = null;
				}, 0);
			});
	}, [params, refetch]);

	useEffect(() => {
		if (!queryIsFetching && !isRefetching) {
			setCurrentData(query.data);
		}
	}, [query.data, queryIsFetching, isRefetching]);

	useEffect(() => {
		latestDataRef.current = query.data;
	}, [query.data]);

	useEffect(() => {
		return () => {
			if (refetchResetTimeoutRef.current) {
				clearTimeout(refetchResetTimeoutRef.current);
				refetchResetTimeoutRef.current = null;
			}
		};
	}, []);

	const setParams = (value: Updater<WorkOrdersReportParams>) => {
		setParamsState(prev => toUpdaterValue(value, prev));
	};

	const reset = () => {
		setParamsState(defaults);
	};

	return {
		data: (isRefetching ? currentData : (currentData ?? data)),
		isLoading,
		isFetching: queryIsFetching || isRefetching,
		error,
		params,
		setParams,
		reset,
	};
}
